/**
 * Data layer for `manage-campaign-section-centralized-dashboard`. Thin typed fetch wrappers over /api/v1/manage-campaign-section-centralized-dashboard.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ManageCampaignSectionCentralizedDashboardRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListManageCampaignSectionCentralizedDashboardResponse {
	count: number;
	items: ManageCampaignSectionCentralizedDashboardRow[];
}

export interface CreateManageCampaignSectionCentralizedDashboardBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/manage-campaign-section-centralized-dashboard";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListManageCampaignSectionCentralizedDashboardResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListManageCampaignSectionCentralizedDashboardResponse>(res);
}

export async function create(body: CreateManageCampaignSectionCentralizedDashboardBody): Promise<ManageCampaignSectionCentralizedDashboardRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ManageCampaignSectionCentralizedDashboardRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const manageCampaignSectionCentralizedDashboardQueryKeys = {
	all: ["manage-campaign-section-centralized-dashboard"] as const,
	list: () => [...manageCampaignSectionCentralizedDashboardQueryKeys.all] as const,
	detail: (id: string) => [...manageCampaignSectionCentralizedDashboardQueryKeys.all, id] as const,
};
